import { useState, useEffect } from "react";
import { toast } from "sonner";
import { getEmailProcesses, updateEmailProcess, getEmailRecipients, addEmailRecipient, updateEmailRecipient, deleteEmailRecipient, createEmailProcess, deleteEmailProcess, sendNotificationEmail } from "../../lib/api";
import { Save, Mail, Code, Plus, Trash2, ToggleRight, ToggleLeft, ChevronDown, ChevronRight, Eye, Send, Loader2 } from "lucide-react";


const PROCESS_VARIABLES = {
  new_lead: ["{name}", "{email}", "{phone}", "{service}", "{message}", "{contactMethod}", "{date}"],
  new_faq_question: ["{name}", "{email}", "{question}", "{date}"],
  faq_answer: ["{name}", "{email}", "{question}", "{answer}", "{date}"]
};

export default function AdminEmailSettings() {
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedProcess, setExpandedProcess] = useState(null);
  const [showTemplate, setShowTemplate] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newProcessKey, setNewProcessKey] = useState("");
  const [newProcessLabel, setNewProcessLabel] = useState("");
  const [showAddProcess, setShowAddProcess] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testingProcess, setTestingProcess] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const processesData = await getEmailProcesses();
        const processesWithRecipients = await Promise.all(
          processesData.map(async (p) => {
            const recipients = await getEmailRecipients(p.id);
            return { ...p, recipients: recipients || [] };
          })
        );
        setProcesses(processesWithRecipients);
      } catch (err) {
        console.error(err);
        toast.error("Erro ao carregar configurações");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function handleSaveProcess(process) {
    setSaving(true);
    try {
      const updated = await updateEmailProcess(process.id, {
        enabled: process.enabled,
        email_subject: process.email_subject,
        email_from_name: process.email_from_name,
        email_from_address: process.email_from_address,
        email_template: process.email_template
      });
      setProcesses((p) => p.map((item) => item.id === process.id ? { ...item, ...updated } : item));
      toast.success("Processo salvo");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao salvar");
    } finally {
      setSaving(false);
    }
  }

  async function handleAddRecipient(processId) {
    if (!newEmail.trim()) return toast.error("Email obrigatório");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) return toast.error("Email inválido");
    try {
      const added = await addEmailRecipient({ process_id: processId, email: newEmail.trim(), label: newLabel.trim() });
      setProcesses((p) => p.map((proc) =>
        proc.id === processId ? { ...proc, recipients: [...proc.recipients, added] } : proc
      ));
      setNewEmail("");
      setNewLabel("");
      toast.success("Destinatário adicionado");
    } catch (err) {
      toast.error("Erro ao adicionar");
    }
  }

  async function handleToggleRecipient(processId, recipient) {
    try {
      const updated = await updateEmailRecipient(recipient.id, { ...recipient, enabled: !recipient.enabled });
      setProcesses((p) => p.map((proc) =>
        proc.id === processId ? { ...proc, recipients: proc.recipients.map((r) => r.id === recipient.id ? updated : r) } : proc
      ));
    } catch (err) {
      toast.error("Erro ao atualizar");
    }
  }

  async function handleDeleteRecipient(processId, id) {
    try {
      await deleteEmailRecipient(id);
      setProcesses((p) => p.map((proc) =>
        proc.id === processId ? { ...proc, recipients: proc.recipients.filter((r) => r.id !== id) } : proc
      ));
      toast.success("Destinatário removido");
    } catch (err) {
      toast.error("Erro ao remover");
    }
  }

  async function handleCreateProcess() {
    if (!newProcessKey.trim()) return toast.error("Chave obrigatória");
    if (!newProcessLabel.trim()) return toast.error("Nome obrigatório");
    try {
      const created = await createEmailProcess({
        process_key: newProcessKey.trim().toLowerCase().replace(/\s+/g, '_'),
        label: newProcessLabel.trim(),
        enabled: true,
        email_subject: 'Novo evento — ' + newProcessKey.trim(),
        email_from_name: 'Hub Profissional',
        email_from_address: 'noreply@eprates.com',
        email_template: ''
      });
      setProcesses((p) => [...p, { ...created, recipients: [] }]);
      setNewProcessKey("");
      setNewProcessLabel("");
      setShowAddProcess(false);
      toast.success("Processo criado");
    } catch (err) {
      if (err.message?.includes('duplicate')) toast.error("Já existe um processo com essa chave");
      else toast.error("Erro ao criar processo");
    }
  }

  async function handleDeleteProcess(id) {
    try {
      await deleteEmailProcess(id);
      setProcesses((p) => p.filter((proc) => proc.id !== id));
      if (expandedProcess === id) setExpandedProcess(null);
      toast.success("Processo removido");
    } catch (err) {
      toast.error("Erro ao remover");
    }
  }

  function updateProcess(processId, key, value) {
    setProcesses((p) => p.map((proc) =>
      proc.id === processId ? { ...proc, [key]: value } : proc
    ));
  }

  async function handleTestSend(proc) {
    if (!testEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmail.trim())) {
      return toast.error("Informe um email válido para o teste");
    }
    if (!proc.email_template) {
      return toast.error("Template de email não configurado");
    }
    setTestingProcess(proc.id);
    try {
      const subject = (proc.email_subject || 'Teste — {process}')
        .replace('{process}', proc.label)
        .replace('{name}', 'João Silva')
        .replace('{service}', 'Consultoria Imobiliária');
      const fromName = proc.email_from_name || 'Hub Profissional';
      const fromAddress = proc.email_from_address || 'noreply@eprates.com';
      const template = proc.email_template;

      const html = template
        .replace(/\{name\}/g, 'João Silva')
        .replace(/\{email\}/g, 'joao@exemplo.com')
        .replace(/\{phone\}/g, '+351 912 345 678')
        .replace(/\{service\}/g, 'Consultoria Imobiliária')
        .replace(/\{message\}/g, 'Olá, gostaria de saber mais sobre os serviços disponíveis.')
        .replace(/\{contactMethod\}/g, 'WhatsApp')
        .replace(/\{question\}/g, 'Como funciona o processo de consultoria?')
        .replace(/\{answer\}/g, 'O processo começa com uma reunião gratuita para entender suas necessidades.')
        .replace(/\{date\}/g, new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }));

      await sendNotificationEmail(
        testEmail.trim(), subject, html,
        fromAddress, fromName, fromAddress, fromName
      );
      toast.success("Email de teste enviado para " + testEmail.trim());
      setTestEmail("");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao enviar email de teste");
    } finally {
      setTestingProcess(null);
    }
  }

  if (loading) return <div className="py-12 text-center text-warmgray text-sm">Carregando...</div>;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-white border border-fendi p-4 md:p-8 max-w-4xl shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
          <p className="eyebrow flex items-center gap-2 text-champagne">
            <Mail size={16} /> Processos de Email
          </p>
          <button
            type="button"
            onClick={() => setShowAddProcess(!showAddProcess)}
            className="text-xs text-champagne hover:text-softblack transition-colors flex items-center gap-1 self-start sm:self-auto"
          >
            <Plus size={12} /> Novo processo
          </button>
        </div>
        <p className="text-xs text-warmgray">Cada processo tem seu próprio template, assunto e destinatários</p>
      </div>

      {/* Add new process */}
      {showAddProcess && (
        <div className="bg-white border border-fendi p-4 md:p-8 max-w-4xl shadow-sm">
          <p className="eyebrow mb-6 text-champagne">Novo Processo</p>
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
            <label className="flex-1 block">
              <span className="eyebrow text-[10px] text-warmgray">Chave (única)</span>
              <input className="editorial-field mt-3" value={newProcessKey} onChange={(e) => setNewProcessKey(e.target.value)} placeholder="ex: new_lead, password_change" />
            </label>
            <label className="flex-1 block">
              <span className="eyebrow text-[10px] text-warmgray">Nome</span>
              <input className="editorial-field mt-3" value={newProcessLabel} onChange={(e) => setNewProcessLabel(e.target.value)} placeholder="ex: Novo Lead" />
            </label>
            <button type="button" onClick={handleCreateProcess} className="btn-primary !px-4 !py-3 h-[50px] w-full sm:w-auto"><Plus size={16} /></button>
          </div>
        </div>
      )}

      {/* Process list */}
      {processes.length === 0 && (
        <div className="bg-white border border-fendi p-8 max-w-4xl shadow-sm text-center text-warmgray text-sm">
          Nenhum processo configurado
        </div>
      )}

      {processes.map((proc) => {
        const isExpanded = expandedProcess === proc.id;
        const variables = PROCESS_VARIABLES[proc.process_key] || ["{name}", "{email}", "{phone}", "{service}", "{message}", "{contactMethod}", "{date}"];

        return (
          <div key={proc.id} className="bg-white border border-fendi max-w-4xl shadow-sm overflow-hidden">
            {/* Process header */}
            <button
              type="button"
              onClick={() => setExpandedProcess(isExpanded ? null : proc.id)}
              className="w-full flex items-center justify-between p-4 md:p-6 hover:bg-fendi/5 transition-colors"
            >
              <div className="flex items-center gap-3 md:gap-4 min-w-0">
                <div className={`w-2 h-2 rounded-full shrink-0 ${proc.enabled ? 'bg-champagne' : 'bg-fendi'}`} />
                <div className="text-left min-w-0">
                  <p className="text-sm font-medium text-softblack truncate">{proc.label}</p>
                  <p className="text-[11px] md:text-xs text-warmgray font-mono truncate">{proc.process_key}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 md:gap-3 shrink-0">
                <span className="text-[10px] md:text-xs text-warmgray whitespace-nowrap">{proc.recipients?.length || 0}</span>
                {isExpanded ? <ChevronDown size={14} className="text-warmgray" /> : <ChevronRight size={14} className="text-warmgray" />}
              </div>
            </button>

            {/* Expanded content */}
            {isExpanded && (
              <div className="border-t border-fendi/20 p-4 md:p-6 space-y-5 md:space-y-6">
                {/* Toggle + delete */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" checked={proc.enabled || false} onChange={(e) => updateProcess(proc.id, "enabled", e.target.checked)} className="sr-only peer" />
                      <div className="w-11 h-6 bg-fendi peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-champagne"></div>
                    </label>
                    <span className="text-xs text-warmgray">{proc.enabled ? 'Ativado' : 'Desativado'}</span>
                  </div>
                  <button type="button" onClick={() => handleDeleteProcess(proc.id)} className="text-xs text-warmgray hover:text-destructive transition-colors flex items-center gap-1">
                    <Trash2 size={12} /> Remover
                  </button>
                </div>

                {/* Subject */}
                <label className="block">
                  <span className="eyebrow text-[10px] text-warmgray">Assunto</span>
                  <input className="editorial-field mt-3" value={proc.email_subject || ""} onChange={(e) => updateProcess(proc.id, "email_subject", e.target.value)} placeholder="Novo Lead — {service}" />
                </label>

                {/* From name / address */}
                <div className="grid md:grid-cols-2 gap-8">
                  <label className="block">
                    <span className="eyebrow text-[10px] text-warmgray">Nome do Remetente</span>
                    <input className="editorial-field mt-3" value={proc.email_from_name || ""} onChange={(e) => updateProcess(proc.id, "email_from_name", e.target.value)} placeholder="Hub Profissional" />
                  </label>
                  <label className="block">
                    <span className="eyebrow text-[10px] text-warmgray">Email do Remetente</span>
                    <input className="editorial-field mt-3" value={proc.email_from_address || ""} onChange={(e) => updateProcess(proc.id, "email_from_address", e.target.value)} placeholder="noreply@eprates.com" />
                  </label>
                </div>

                {/* Recipients */}
                <div className="border-t border-fendi/10 pt-6">
                  <p className="eyebrow mb-4 text-champagne">Destinatários</p>
                  <div className="flex flex-col sm:flex-row gap-3 mb-4 items-stretch sm:items-end">
                    <label className="flex-1 block">
                      <span className="eyebrow text-[10px] text-warmgray">Email</span>
                      <input className="editorial-field mt-3" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="email@exemplo.com" onKeyDown={(e) => e.key === "Enter" && handleAddRecipient(proc.id)} />
                    </label>
                    <label className="sm:w-40 block">
                      <span className="eyebrow text-[10px] text-warmgray">Etiqueta</span>
                      <input className="editorial-field mt-3" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Principal" onKeyDown={(e) => e.key === "Enter" && handleAddRecipient(proc.id)} />
                    </label>
                    <button type="button" onClick={() => handleAddRecipient(proc.id)} className="btn-primary !px-4 !py-3 h-[50px] w-full sm:w-auto"><Plus size={16} /></button>
                  </div>
                  <div className="space-y-2">
                    {(proc.recipients || []).length === 0 && <p className="text-xs text-warmgray text-center py-4">Nenhum destinatário</p>}
                    {(proc.recipients || []).map((r) => (
                      <div key={r.id} className={`flex items-center justify-between p-3 border transition-colors ${r.enabled ? "border-fendi/30 bg-white" : "border-fendi/10 bg-fendi/5 opacity-60"}`}>
                        <div className="flex items-center gap-3">
                          <button type="button" onClick={() => handleToggleRecipient(proc.id, r)} className="text-warmgray hover:text-softblack">
                            {r.enabled ? <ToggleRight size={20} className="text-champagne" /> : <ToggleLeft size={20} />}
                          </button>
                          <div>
                            <p className="text-sm text-softblack">{r.email}</p>
                            {r.label && <p className="text-[10px] text-warmgray">{r.label}</p>}
                          </div>
                        </div>
                        <button type="button" onClick={() => handleDeleteRecipient(proc.id, r.id)} className="text-warmgray hover:text-destructive p-1"><Trash2 size={14} /></button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Template */}
                <div className="border-t border-fendi/10 pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <p className="eyebrow text-champagne">Template HTML</p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setShowTemplate("code")}
                        className={`text-xs px-3 py-1.5 border transition-colors ${showTemplate === "code" ? "bg-champagne/10 border-champagne text-softblack" : "border-fendi text-warmgray hover:text-softblack"}`}
                      >
                        <Code size={12} className="inline mr-1" /> Código
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowTemplate("design")}
                        className={`text-xs px-3 py-1.5 border transition-colors ${showTemplate === "design" ? "bg-champagne/10 border-champagne text-softblack" : "border-fendi text-warmgray hover:text-softblack"}`}
                      >
                        <Eye size={12} className="inline mr-1" /> Design
                      </button>
                    </div>
                  </div>

                  {showTemplate === "code" && (
                    <div className="space-y-3">
                      <p className="text-[10px] text-warmgray">Variáveis: {variables.map((v) => <code key={v} className="bg-fendi/20 px-1 py-0.5 rounded mr-1">{v}</code>)}</p>
                      <textarea className="editorial-field font-mono text-[10px] leading-relaxed" rows={16} value={proc.email_template || ""} onChange={(e) => updateProcess(proc.id, "email_template", e.target.value)} placeholder="Cole seu HTML aqui..." />
                    </div>
                  )}

                  {showTemplate === "design" && (
                    <div className="space-y-3">
                      <p className="text-[10px] text-warmgray">Pré-visualização com dados de exemplo</p>
                      <div className="border border-fendi/30 rounded-lg overflow-hidden bg-gray-50">
                        <div
                          className="w-full"
                          style={{ minHeight: "400px" }}
                          dangerouslySetInnerHTML={{
                            __html: (proc.email_template || '')
                              .replace(/\{name\}/g, 'João Silva')
                              .replace(/\{email\}/g, 'joao@exemplo.com')
                              .replace(/\{phone\}/g, '+351 912 345 678')
                              .replace(/\{service\}/g, 'Consultoria Imobiliária')
                              .replace(/\{message\}/g, 'Olá, gostaria de saber mais sobre os serviços disponíveis.')
                              .replace(/\{contactMethod\}/g, 'WhatsApp')
                              .replace(/\{date\}/g, new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }))
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Save */}
                <div className="flex justify-between items-center pt-4 border-t border-fendi/10">
                  <div className="flex items-center gap-3">
                    <input
                      className="editorial-field !py-2 !text-sm max-w-[260px]"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="email@teste.com"
                      onKeyDown={(e) => e.key === "Enter" && handleTestSend(proc)}
                    />
                    <button
                      type="button"
                      onClick={() => handleTestSend(proc)}
                      disabled={testingProcess === proc.id}
                      className="text-xs flex items-center gap-1 px-4 py-2 border border-fendi/40 text-warmgray hover:text-softblack hover:border-fendi transition-colors disabled:opacity-50"
                    >
                      {testingProcess === proc.id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                      Testar Envio
                    </button>
                  </div>
                  <button onClick={() => handleSaveProcess(proc)} disabled={saving} className="btn-primary !px-10">
                    <Save size={14} className="mr-3" /> {saving ? "Salvando..." : "Salvar"}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
